/**
 * cashSeries — dérivation partagée des séries du Cash Outlook.
 *
 * Extrait de SynthesisTab : à partir du `calc` (moteur) + des projections HR,
 * produit toutes les séries mensuelles, totaux, KPI et lignes de détail
 * affichés dans le tableau. Pur (pas de JSX, pas de hooks) → réutilisé par
 * SynthesisTab (écran) ET CashForecastPrint (PDF) pour des chiffres identiques.
 */

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const EN_MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function englishMonthLabels(startMonth = 0) {
  const s = Number.isInteger(startMonth) && startMonth >= 0 && startMonth <= 11 ? startMonth : 0;
  return [...Array(12)].map((_, i) => EN_MONTHS_SHORT[(s + i) % 12]);
}

// Encaissement HT d'une ligne de revenu en ordre scénario (rotation + lag TVA).
export function lineEncaisseScenarioOrder(rev, startMonth) {
  const out = Array(12).fill(0);
  const values = rev.monthly_values || Array(12).fill(0);
  const lag = Number(rev.payment_lag) || 0;
  const s = Number.isInteger(startMonth) ? startMonth : 0;
  for (let mCal = 0; mCal < 12; mCal += 1) {
    const offset = (mCal - s + 12) % 12;
    const dest = offset + lag;
    if (dest < 12) out[dest] += Number(values[mCal]) || 0;
  }
  return out;
}

// Décaissement HT d'une charge variable manuelle, en ordre scénario.
export function lineDecaisseManualScenarioOrder(line, startMonth) {
  const out = Array(12).fill(0);
  const values = line.monthly_values || Array(12).fill(0);
  const lag = Number(line.payment_lag) || 0;
  const s = Number.isInteger(startMonth) ? startMonth : 0;
  for (let mCal = 0; mCal < 12; mCal += 1) {
    const offset = (mCal - s + 12) % 12;
    const dest = offset + lag;
    if (dest < 12) out[dest] += Number(values[mCal]) || 0;
  }
  return out;
}

// Décaissement HT d'une charge variable en %CA (taux × CA HT du revenu lié).
export function lineDecaissePctRevScenarioOrder(line, revenuesById, startMonth) {
  const out = Array(12).fill(0);
  const rev = revenuesById[line.revenue_ref_id];
  if (!rev) return out;
  const revArr = lineEncaisseScenarioOrder({ ...rev, payment_lag: 0 }, startMonth);
  const rate = Number(line.rate) || 0;
  // Coût marchandise sur la part marchandise (goods_pct 0..1).
  const g = rev.goods_pct == null ? 1 : Number(rev.goods_pct);
  const lag = Number(line.payment_lag) || 0;
  for (let m = 0; m < 12; m += 1) {
    const ht = revArr[m] * rate * g;
    const dest = m + lag;
    if (dest < 12) out[dest] += ht;
  }
  return out;
}

export function deriveCashSeries({
  scenario,
  calc,
  revenues = [],
  variables = [],
  personnelProjection = null,
  chargesSocialesMonthly = null,
  assurancesMonthly = null,
  annualExtrasMonthly = null,
  // Liquidités N-1 du bilan, par année civile : { [year]: [12 soldes fin de
  // mois, index 0 = janvier] }. Sert à tracer la trajectoire de l'an passé en
  // regard du forecast (même mois civil, un an plus tôt). Absent = null (pas de
  // repli) → la ligne ne s'affiche pas pour les mois sans donnée bilan.
  bsLiquidityByYear = {},
  // Durée de projection affichée/synthétisée : 3, 6 ou 12 mois. Tronque la
  // fenêtre scénario en tête de fonction → tableau, graphique, totaux et KPI
  // suivent tous. Le moteur calcule toujours 12 mois ; on en garde les H premiers.
  horizon = 12,
}) {
  if (!calc) return null;

  const H = Math.min(Math.max(Math.round(Number(horizon) || 12), 1), calc.soldes.length);
  const slc = (a) => (Array.isArray(a) ? a.slice(0, H) : a);
  const soldes = calc.soldes.slice(0, H);

  const startMonth = Number.isInteger(scenario?.start_month) ? scenario.start_month : 0;
  const monthLabels = englishMonthLabels(startMonth).slice(0, H);
  const startYear = scenario?.start_year || new Date().getFullYear();
  const soldeInit = num(scenario?.initial_balance);

  // ── People expenses (fixed + paid indemnities + other) — ligne dédiée,
  //    hors groupe Payroll, mais comptée dans les outflows ──
  const expensesMonthly = slc(annualExtrasMonthly?.expenses || Array(H).fill(0));
  const persExpenses = expensesMonthly.reduce((a, b) => a + num(b), 0);

  // ── Extras (compléments soumis CS + bonus) — ligne distincte de Payroll ──
  const varCompMonthly = slc(annualExtrasMonthly?.varComp || Array(H).fill(0));
  const bonusMonthly   = slc(annualExtrasMonthly?.bonus   || Array(H).fill(0));
  const extrasMonthly  = Array.from({ length: H }, (_, k) =>
    num(varCompMonthly[k]) + num(bonusMonthly[k]));
  const persExtras = extrasMonthly.reduce((a, b) => a + num(b), 0);

  // ── HR override propagation (net + CS + assurances ; extras séparés) ──
  const persChartOverride = personnelProjection
    ? Array.from({ length: H }, (_, k) =>
        num((personnelProjection.net || [])[k])
        + num((chargesSocialesMonthly || [])[k])
        + num((assurancesMonthly || [])[k]))
    : null;
  const effectiveDecMonthly = soldes.map((s, i) => {
    const decPersV = persChartOverride ? persChartOverride[i] : num(s.decPers);
    return num(s.decExp) + decPersV + num(s.decTva) + num(expensesMonthly[i]) + extrasMonthly[i];
  });
  const effectiveFluxNetMonthly = soldes.map((s, i) => num(s.enc) - effectiveDecMonthly[i]);
  const effectiveSoldeMonthly = (() => {
    const out = [];
    let acc = soldeInit;
    for (let i = 0; i < H; i += 1) { acc += effectiveFluxNetMonthly[i]; out.push(acc); }
    return out;
  })();

  const soldeFinal = effectiveSoldeMonthly[effectiveSoldeMonthly.length - 1] ?? soldeInit;
  const soldeMin = Math.min(...effectiveSoldeMonthly);
  const soldeMinMois = effectiveSoldeMonthly.indexOf(soldeMin);
  const burnRate = effectiveDecMonthly.reduce((a, b) => a + b, 0) / H;
  const runway = burnRate > 0 ? soldeInit / burnRate : 0;
  const soldeMinLabel = monthLabels[soldeMinMois] || '—';

  // ── Encaissements par nature + coût marchandise, par mois (pour les vues Marge) ──
  const splitEncMonthly = (cat) => {
    const m = Array(12).fill(0);
    revenues.forEach((r) => {
      const g = Number(r.goods_pct) || 0;
      const share = cat === 'goods' ? g : 1 - g;
      if (share <= 0) return;
      lineEncaisseScenarioOrder(r, startMonth).forEach((v, i) => { m[i] += v * share; });
    });
    return m;
  };
  const svcEncMonthly = splitEncMonthly('services');
  const goodsEncMonthly = splitEncMonthly('goods');
  const revByIdEarly = revenues.reduce((acc, r) => ({ ...acc, [r.id]: r }), {});
  const cogsMonthly = Array(12).fill(0);
  variables
    .filter((v) => v.driver_type === 'pct_revenue' && v.affects_cash !== false)
    .forEach((v) => lineDecaissePctRevScenarioOrder(v, revByIdEarly, startMonth)
      .forEach((x, i) => { cogsMonthly[i] += x; }));

  // ── Chart data ──
  const chartData = soldes.map((s, i) => {
    const calMonth = (startMonth + i) % 12;
    const year = startYear + (startMonth + i >= 12 ? 1 : 0);
    const solde = effectiveSoldeMonthly[i];
    // Liquidité du bilan au même mois civil l'an passé (year-1). null si la
    // donnée bilan n'existe pas → la ligne N-1 s'interrompt proprement.
    const prevYear = bsLiquidityByYear[year - 1];
    const liqN1 = prevYear && Number.isFinite(prevYear[calMonth]) ? prevYear[calMonth] : null;
    const decPersonnel = (persChartOverride ? persChartOverride[i] : num(s.decPers))
      + extrasMonthly[i] + num(expensesMonthly[i]);
    return {
      label: `${monthLabels[i]} ${year}`,
      monthShort: monthLabels[i],
      year,
      crossesYear: i > 0 && calMonth === 0,
      enc: num(s.enc),
      dec: effectiveDecMonthly[i],
      decVariables: num(s.decVariablesHT),
      fluxNet: effectiveFluxNetMonthly[i],
      solde,
      liqN1,
      manco: solde < 0 ? Math.abs(solde) : 0,
      // Per-category — used by chart filter pills
      encProduits:  num(s.encHT),
      encTVA:       num(s.encTVA),
      encOpening:   num(s.encOpening),
      // Split prestation / marchandise (vues Marge)
      encServices:  num(svcEncMonthly[i]),
      encGoods:     num(goodsEncMonthly[i]),
      cogsMarch:    num(cogsMonthly[i]),
      decPersonnel,
      decFixes:     num(s.decMensuelHT) + num(s.decSpecifiqueHT) + num(s.decVariablesHT),
      decTVAOut:    num(s.decTva) + num(s.decExpTVA),
      decOpeningOut: num(s.decOpening),
    };
  });

  const total = (key) => soldes.reduce((a, s) => a + num(s[key]), 0);

  const totalEnc = total('enc');
  const totalEncHT = total('encHT');
  const totalEncTVA = total('encTVA');
  const totalEncOpening = total('encOpening');
  const totalDecPers = total('decPers');
  const totalDecTva = total('decTva');
  const totalDecOpening = total('decOpening');
  const totalDecVariablesHT = total('decVariablesHT');
  const totalDecMensuelHT = total('decMensuelHT');
  const totalDecSpecifiqueHT = total('decSpecifiqueHT');
  const totalDecExpTVA = total('decExpTVA');
  const totalDecExp = total('decExp');

  // ── Personnel sub-totals (HR override aware) ──
  const salairesNetsLegacy = slc(calc.salairesNets || Array(H).fill(0));
  const salairesNets = slc(personnelProjection?.net || salairesNetsLegacy);
  const chargesEmprLegacy = slc(calc.chargesEmployeurMensuel || Array(H).fill(0));
  const chargesEmpeLegacy = slc(calc.chargesEmployeMensuel || Array(H).fill(0));
  const csTotalMonthly = Array.isArray(chargesSocialesMonthly)
    ? slc(chargesSocialesMonthly)
    : chargesEmprLegacy.map((v, i) => num(v) + num(chargesEmpeLegacy[i]));
  const assurances = Array.isArray(assurancesMonthly)
    ? slc(assurancesMonthly)
    : slc(calc.assurDecaissement || Array(H).fill(0));
  const persSal = salairesNets.reduce((a, b) => a + num(b), 0);
  const persCs = csTotalMonthly.reduce((a, b) => a + num(b), 0);
  const persAss = assurances.reduce((a, b) => a + num(b), 0);

  const persVarComp = varCompMonthly.reduce((a, b) => a + num(b), 0);
  const persBonus = bonusMonthly.reduce((a, b) => a + num(b), 0);

  // effectiveDecPers = Charges employeurs = net + CS + assurances (sans extras).
  const decPersFromParts = personnelProjection
    ? salairesNets.map((s, i) =>
        num(s) + num(csTotalMonthly[i]) + num(assurances[i]))
    : null;
  const effectiveDecPers = decPersFromParts || soldes.map((s) => num(s.decPers));
  const effectiveTotalDecPers = effectiveDecPers.reduce((a, b) => a + b, 0);
  const persOther = personnelProjection ? 0 : (totalDecPers - persSal - persCs - persAss);

  const decExploitMonthly = soldes.map((s, i) =>
    num(s.decExp) + num(effectiveDecPers[i]) + extrasMonthly[i] + num(expensesMonthly[i]));
  const totalDecExploit = totalDecExp + effectiveTotalDecPers + persExtras + persExpenses;
  const totalDec = totalDecExploit + totalDecTva;
  const totalFluxNet = totalEnc - totalDec;
  const hasOpeningRec = totalEncOpening > 0.5;
  const hasOpeningPay = totalDecOpening > 0.5;

  // ── Encaissements agrégés par nature (2 lignes max) ──
  // Chaque ligne porte une part marchandise goods_pct (0..1) ; on répartit son
  // encaissement : part marchandise = ×goods_pct, part prestation = ×(1−goods_pct).
  const sumCategory = (cat) => {
    const monthly = Array(12).fill(0);
    revenues.forEach((r) => {
      const g = Number(r.goods_pct) || 0;
      const share = cat === 'goods' ? g : 1 - g;
      if (share <= 0) return;
      lineEncaisseScenarioOrder(r, startMonth).forEach((v, i) => { monthly[i] += v * share; });
    });
    return slc(monthly);
  };
  const revenueDetails = [
    { id: '_services', label: 'Prestations',         monthly: sumCategory('services') },
    { id: '_goods',    label: 'Ventes marchandises',  monthly: sumCategory('goods') },
  ].filter((d) => d.monthly.some((v) => v > 0));
  const revenuesById = revenues.reduce((acc, r) => ({ ...acc, [r.id]: r }), {});
  const variableDetails = variables
    .filter((v) => v.affects_cash !== false) // hypothèse de marge non décaissée → hors cash
    .map((v) => {
      const monthly = slc(v.driver_type === 'pct_revenue'
        ? lineDecaissePctRevScenarioOrder(v, revenuesById, startMonth)
        : lineDecaisseManualScenarioOrder(v, startMonth));
      return { id: v.id, label: v.label || 'Untitled', monthly };
    })
    .filter((v) => v.monthly.some((x) => x > 0));

  return {
    horizon: H,
    soldes, // soldes moteur tronqués à H (lignes brutes du tableau)
    startMonth, monthLabels, startYear, soldeInit,
    effectiveDecMonthly, effectiveFluxNetMonthly, effectiveSoldeMonthly,
    soldeFinal, soldeMin, soldeMinMois, soldeMinLabel, burnRate, runway,
    chartData,
    totalEnc, totalEncHT, totalEncTVA, totalEncOpening,
    totalDecPers, totalDecTva, totalDecOpening, totalDecVariablesHT,
    totalDecMensuelHT, totalDecSpecifiqueHT, totalDecExpTVA, totalDecExp,
    salairesNets, csTotalMonthly, assurances,
    persSal, persCs, persAss,
    varCompMonthly, bonusMonthly, extrasMonthly, expensesMonthly,
    persVarComp, persBonus, persExtras, persExpenses, persOther,
    effectiveDecPers, effectiveTotalDecPers,
    decExploitMonthly, totalDecExploit, totalDec, totalFluxNet,
    hasOpeningRec, hasOpeningPay,
    revenueDetails, revenuesById, variableDetails,
  };
}
